// src/components/PriceBar.jsx
import React from "react";

/**
 * Props:
 *   percentage    {number}  – (driverOffer / customerPrice) * 100
 *   customerPrice {number}  – السعر اللي العميل دفعه (100% reference)
 *   t             {func}    – i18next translate function
 */
const PriceBar = ({ percentage, customerPrice, t }) => {
  if (percentage == null || customerPrice == null) return null;

  // ─── حدود المناطق (dynamic على حسب customerPrice) ───────────────────────
  // 🟢  <= 65%   |  🟡 66–80%  |  🔴 81–100%  |  🔴 OVER > 100%
  const GREEN_MAX  = 65;   // %
  const YELLOW_MAX = 80;   // %
  const RED_MAX    = 100;  // %

  // ─── Label & Color ────────────────────────────────────────────────────────
  let labelKey;
  let labelColor;
  let zoneBg;

  if (percentage <= GREEN_MAX) {
    labelKey   = "driverCard.priceStatus.attractive";
    labelColor = "#16a34a";
    zoneBg     = "#16a34a";
  } else if (percentage <= YELLOW_MAX) {
    labelKey   = "driverCard.priceStatus.average";
    labelColor = "#d97706";
    zoneBg     = "#d97706";
  } else if (percentage <= RED_MAX) {
    labelKey   = "driverCard.priceStatus.high";
    labelColor = "#dc2626";
    zoneBg     = "#dc2626";
  } else {
    labelKey   = "driverCard.priceStatus.overBudget";
    labelColor = "#dc2626";
    zoneBg     = "#dc2626";
  }

  // ─── موضع النقطة على الـ bar ─────────────────────────────────────────────
  // Bar = 3 أقسام: 🟢 0→65% | 🟡 65→80% | 🔴 80→100%
  // نحول الـ percentage الفعلية لموضع على bar من 0% → 100%
  // لو عدى 100% نثبت النقطة عند آخر الأحمر (100%)
  const clampedPct   = Math.min(percentage, RED_MAX); // نوقف عند 100%
  const dotPosition  = clampedPct; // 0% = يسار أخضر، 100% = يمين أحمر

  // حدود الـ price بالأرقام
  const greenLimit  = (GREEN_MAX  / 100) * customerPrice;   // 65%  من customer
  const yellowLimit = (YELLOW_MAX / 100) * customerPrice;   // 80%  من customer
  const redLimit    = customerPrice;                         // 100% من customer

  // ─── Gradient: أخضر → أصفر → أحمر (يسار → يمين) ───────────────────────
  // GREEN  0→65%   →  bar position 0→65%
  // YELLOW 65→80%  →  bar position 65→80%
  // RED    80→100% →  bar position 80→100%

  return (
    <div style={{ padding: "8px 4px 0" }}>

      {/* Header */}
      <div style={{ marginBottom: "16px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 2px" }}>
          {t("driverCard.priceStatus.barTitle", "Preisbewertung")}
        </h3>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
          {t("driverCard.priceStatus.barSubtitle", "So attraktiv ist Ihr Angebot für den Versender.")}
        </p>
      </div>

      {/* Zone labels أعلى */}
      <div style={{ display: "flex", marginBottom: "4px" }}>
        <span style={{ flex: GREEN_MAX,         fontSize: "11px", fontWeight: 600, color: "#16a34a", textAlign: "left" }}>
          {t("driverCard.priceStatus.zoneGreen",  "Sehr attraktiv")}
        </span>
        <span style={{ flex: YELLOW_MAX - GREEN_MAX,  fontSize: "11px", fontWeight: 600, color: "#d97706", textAlign: "center" }}>
          {t("driverCard.priceStatus.zoneYellow", "Durchschnitt")}
        </span>
        <span style={{ flex: RED_MAX - YELLOW_MAX,    fontSize: "11px", fontWeight: 600, color: "#dc2626", textAlign: "right" }}>
          {t("driverCard.priceStatus.zoneRed",    "Zu hoch")}
        </span>
      </div>

      {/* Bar */}
      <div style={{ position: "relative", height: "14px", borderRadius: "7px", overflow: "visible", marginBottom: "6px" }}>
        {/* Gradient: أخضر يسار → أحمر يمين */}
        <div style={{
          height: "14px",
          borderRadius: "7px",
          background: `linear-gradient(to right,
            #16a34a 0%,
            #84cc16 ${GREEN_MAX}%,
            #eab308 ${GREEN_MAX}%,
            #f97316 ${YELLOW_MAX}%,
            #dc2626 ${YELLOW_MAX}%,
            #dc2626 100%)`,
        }} />

        {/* Dot */}
        <div style={{
          position:        "absolute",
          top:             "50%",
          left:            `${dotPosition}%`,
          transform:       "translate(-50%, -50%)",
          width:           "22px",
          height:          "22px",
          borderRadius:    "50%",
          backgroundColor: zoneBg,
          border:          "3px solid white",
          boxShadow:       "0 1px 6px rgba(0,0,0,0.35)",
          zIndex:          2,
          transition:      "left 0.3s ease",
        }} />
      </div>

      {/* Price markers أسفل الـ bar */}
      {/* <div style={{ position: "relative", height: "18px", marginBottom: "8px" }}>
        <span style={{
          position:   "absolute",
          left:       `${GREEN_MAX}%`,
          transform:  "translateX(-50%)",
          fontSize:   "10px",
          color:      "#6b7280",
          whiteSpace: "nowrap",
        }}>
          {greenLimit.toFixed(0)}€
        </span>

        <span style={{
          position:   "absolute",
          left:       `${YELLOW_MAX}%`,
          transform:  "translateX(-50%)",
          fontSize:   "10px",
          color:      "#6b7280",
          whiteSpace: "nowrap",
        }}>
          {yellowLimit.toFixed(0)}€
        </span>

        <span style={{
          position:   "absolute",
          left:       "100%",
          transform:  "translateX(-100%)",
          fontSize:   "10px",
          color:      "#6b7280",
          whiteSpace: "nowrap",
        }}>
          {redLimit.toFixed(0)}€
        </span>
      </div> */}

      {/* Ihr Preis pointer */}
      <div style={{ position: "relative", height: "28px", marginBottom: "4px" }}>
        <div style={{
          position:   "absolute",
          left:       `${dotPosition}%`,
          transform:  "translateX(-50%)",
          top:        0,
          width:      0, height: 0,
          borderLeft:   "5px solid transparent",
          borderRight:  "5px solid transparent",
          borderBottom: `7px solid ${zoneBg}`,
          transition:   "left 0.3s ease",
        }} />
        <div style={{
          position:   "absolute",
          left:       `${dotPosition}%`,
          transform:  "translateX(-50%)",
          top:        "9px",
          fontSize:   "11px",
          fontWeight: 700,
          color:      zoneBg,
          whiteSpace: "nowrap",
          transition: "left 0.3s ease",
        }}>
          {t("driverCard.priceStatus.yourPrice", "Ihr Preis")}
        </div>
      </div>

      {/* Status message */}
      <div style={{
        marginTop:    "4px",
        padding:      "8px 12px",
        borderRadius: "8px",
        backgroundColor: `${zoneBg}18`,
        borderLeft:   `4px solid ${zoneBg}`,
      }}>
        <span style={{ fontSize: "13px", fontWeight: 600, color: zoneBg }}>
          {t(labelKey)}
        </span>
        {percentage > RED_MAX && (
          <span style={{ fontSize: "12px", color: "#6b7280", display: "block", marginTop: "2px" }}>
            {`${percentage.toFixed(1)}% ${t("driverCard.priceStatus.ofCustomerPrice", "des Kundenpreises")}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default PriceBar;